import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { FileText, Video, Search, RotateCw, ExternalLink, MessageCircle, AlertCircle, Calendar, X } from 'lucide-react'
import { Post } from './types'
import { API_BASE } from '../config'

const SOURCE_LABELS: Record<string, string> = {
  bitcoin_live: 'Bitcoin.Live',
  ctolarsson: 'CTO Larsson',
}

export default function Posts() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [posts, setPosts] = useState<Post[]>([])
  const [loading, setLoading] = useState(true)
  const [reprocessingId, setReprocessingId] = useState<string | null>(null)
  const [selectedPost, setSelectedPost] = useState<Post | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'completed' | 'failed' | 'processing'>('all')
  const [sourceFilter, setSourceFilter] = useState<string>(searchParams.get('source') || 'all')
  const [error, setError] = useState<string | null>(null)

  const fetchPosts = async () => {
    try {
      setLoading(true)
      setError(null)
      const sourceQuery = sourceFilter !== 'all' ? `&source=${encodeURIComponent(sourceFilter)}` : ''
      const res = await fetch(`${API_BASE}/posts?limit=100${sourceQuery}`)
      if (!res.ok) {
        throw new Error('Failed to load posts database')
      }
      const data = await res.json()
      setPosts(data.posts || [])
    } catch (err: any) {
      setError(err.message || 'Server connection failed')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchPosts()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceFilter])

  const handleSourceFilterChange = (value: string) => {
    setSourceFilter(value)
    if (value === 'all') {
      setSearchParams({})
    } else {
      setSearchParams({ source: value })
    }
  }

  const handleReprocess = async (postId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    if (reprocessingId) return
    setReprocessingId(postId)
    try {
      const res = await fetch(`${API_BASE}/posts/${postId}/reprocess`, { method: 'POST' })
      if (!res.ok) {
        throw new Error('Reprocessing failed')
      }
      const updatedPost = await res.json()
      
      // Update local state list
      setPosts((prev) => prev.map((p) => (p.id === postId ? updatedPost : p)))
      
      // Update selected modal view if active
      if (selectedPost?.id === postId) {
        setSelectedPost(updatedPost)
      }
    } catch (err: any) {
      alert(err.message || 'Error occurred during reprocess')
    } finally {
      setReprocessingId(null)
    }
  }

  // Filter posts
  const filteredPosts = posts.filter((post) => {
    const matchesSearch =
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.url.toLowerCase().includes(searchQuery.toLowerCase())
    
    const matchesStatus =
      statusFilter === 'all' || post.status === statusFilter

    return matchesSearch && matchesStatus
  })

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h2 className="text-3xl font-extrabold tracking-tight bg-gradient-to-r from-white via-slate-200 to-slate-400 bg-clip-text text-transparent">
          Processed Archives
        </h2>
        <p className="text-slate-400 text-sm mt-1">
          Explore previously scraped premium posts, check transcripts, summaries, and errors.
        </p>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="p-4 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-400 text-sm flex items-center gap-3">
          <AlertCircle size={18} className="flex-shrink-0" />
          <span><strong>Connection Alert:</strong> {error}</span>
        </div>
      )}

      {/* Controls Bar */}
      <div className="flex flex-col sm:flex-row gap-4 items-center justify-between p-4 glass-panel rounded-2xl">
        {/* Search */}
        <div className="relative w-full sm:w-72">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            type="text"
            placeholder="Search by title or slug..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 text-sm bg-slate-950 border border-slate-850 rounded-xl text-slate-200 focus:outline-none focus:border-cyan-500 transition-colors placeholder:text-slate-650"
          />
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:items-end gap-2 w-full sm:w-auto">
          <div className="flex gap-2 w-full sm:w-auto overflow-x-auto pb-1 sm:pb-0">
            {(['all', 'bitcoin_live', 'ctolarsson'] as const).map((src) => (
              <button
                key={src}
                onClick={() => handleSourceFilterChange(src)}
                className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold uppercase tracking-wider transition-all ${
                  sourceFilter === src
                    ? 'bg-blue-500 text-slate-950 shadow-[0_0_15px_rgba(59,130,246,0.2)]'
                    : 'bg-slate-900/50 hover:bg-slate-900 text-slate-400 hover:text-slate-200 border border-slate-850'
                }`}
              >
                {src === 'all' ? 'all sources' : SOURCE_LABELS[src] || src}
              </button>
            ))}
          </div>
          <div className="flex gap-2 w-full sm:w-auto overflow-x-auto pb-1 sm:pb-0">
            {(['all', 'completed', 'failed', 'processing'] as const).map((filter) => (
              <button
                key={filter}
                onClick={() => setStatusFilter(filter)}
                className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold uppercase tracking-wider transition-all ${
                  statusFilter === filter
                    ? 'bg-cyan-500 text-slate-950 shadow-[0_0_15px_rgba(6,182,212,0.2)]'
                    : 'bg-slate-900/50 hover:bg-slate-900 text-slate-400 hover:text-slate-200 border border-slate-850'
                }`}
              >
                {filter}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Posts Table/Grid */}
      <div className="glass-panel rounded-2xl overflow-hidden">
        {loading ? (
          <div className="py-20 text-center text-slate-500 text-sm flex flex-col items-center gap-3">
            <RotateCw size={24} className="animate-spin text-cyan-400" />
            Loading archive documents...
          </div>
        ) : filteredPosts.length === 0 ? (
          <div className="py-20 text-center text-slate-500 text-sm">
            No items matched your query criteria.
          </div>
        ) : (
          <div className="divide-y divide-slate-850">
            {filteredPosts.map((post) => (
              <div
                key={post.id}
                onClick={() => setSelectedPost(post)}
                className="p-5 flex flex-col md:flex-row md:items-center justify-between gap-4 hover:bg-slate-900/20 cursor-pointer transition-colors group"
              >
                <div className="space-y-1.5 min-w-0 flex-1">
                  <div className="flex items-center gap-2.5">
                    <span className="text-slate-400 group-hover:text-cyan-400 transition-colors">
                      {post.content_type === 'video' ? <Video size={16} /> : <FileText size={16} />}
                    </span>
                    <h4 className="text-base font-bold text-slate-100 group-hover:text-white transition-colors truncate">
                      {post.title}
                    </h4>
                    <span className="text-[9px] px-2 py-0.5 rounded-full border border-slate-800 bg-slate-900/60 text-slate-500 font-bold uppercase tracking-wider flex-shrink-0">
                      {SOURCE_LABELS[post.source || 'bitcoin_live'] || post.source}
                    </span>
                  </div>

                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500 font-medium">
                    <span className="flex items-center gap-1">
                      <Calendar size={12} />
                      {post.processed_at
                        ? new Date(post.processed_at).toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                        : 'Unprocessed'}
                    </span>
                    <span>•</span>
                    <a
                      href={post.display_url || post.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-slate-500 hover:text-cyan-400 flex items-center gap-0.5"
                    >
                      Original post <ExternalLink size={10} />
                    </a>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <span className={`text-[10px] px-2.5 py-0.5 rounded-full border font-bold uppercase tracking-wider ${
                    post.status === 'completed'
                      ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400'
                      : post.status === 'failed'
                      ? 'bg-rose-500/10 border-rose-500/20 text-rose-400'
                      : 'bg-cyan-500/10 border-cyan-500/20 text-cyan-400 animate-pulse'
                  }`}>
                    {post.status}
                  </span>

                  {post.source !== 'ctolarsson' && (
                    <button
                      onClick={(e) => handleReprocess(post.id, e)}
                      disabled={reprocessingId === post.id}
                      className="p-2 bg-slate-900 border border-slate-800 hover:border-slate-700 text-slate-400 hover:text-slate-200 rounded-xl transition-all"
                      title="Reprocess content"
                    >
                      <RotateCw size={14} className={reprocessingId === post.id ? 'animate-spin text-cyan-400' : ''} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Details Modal overlay */}
      {selectedPost && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex justify-end">
          <div className="w-full max-w-2xl bg-slate-950 border-l border-slate-850 h-full flex flex-col shadow-2xl relative">
            
            {/* Modal Header */}
            <div className="p-6 border-b border-slate-850 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-cyan-400">
                  {selectedPost.content_type === 'video' ? <Video size={20} /> : <FileText size={20} />}
                </span>
                <span className="text-xs uppercase tracking-widest text-slate-500 font-bold">Post Details</span>
              </div>
              <button
                onClick={() => setSelectedPost(null)}
                className="p-1.5 hover:bg-slate-900 rounded-lg text-slate-400 hover:text-slate-200 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Body Scroll */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              <div>
                <h3 className="text-xl font-bold text-white leading-snug">{selectedPost.title}</h3>
                <a
                  href={selectedPost.display_url || selectedPost.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-cyan-400 hover:text-cyan-300 inline-flex items-center gap-1 mt-1 font-medium"
                >
                  View original {SOURCE_LABELS[selectedPost.source || 'bitcoin_live'] || 'source'} content <ExternalLink size={11} />
                </a>
              </div>

              {/* Status Section */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-900/30 border border-slate-850 rounded-xl">
                <div>
                  <span className="text-slate-500 text-[10px] uppercase font-bold tracking-wider block">Status</span>
                  <span className={`inline-flex items-center gap-1.5 text-xs font-bold uppercase mt-1 ${
                    selectedPost.status === 'completed' ? 'text-emerald-400' : selectedPost.status === 'failed' ? 'text-rose-400' : 'text-cyan-400'
                  }`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${
                      selectedPost.status === 'completed' ? 'bg-emerald-400' : selectedPost.status === 'failed' ? 'bg-rose-400' : 'bg-cyan-400 animate-ping'
                    }`} />
                    {selectedPost.status}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500 text-[10px] uppercase font-bold tracking-wider block">Telegram Message</span>
                  {selectedPost.telegram_message_id ? (
                    <span className="text-slate-300 text-xs font-mono mt-1 flex items-center gap-1">
                      <MessageCircle size={12} className="text-cyan-400" /> ID: {selectedPost.telegram_message_id}
                    </span>
                  ) : (
                    <span className="text-slate-500 text-xs mt-1 block">Not dispatched</span>
                  )}
                </div>
              </div>

              {/* Error Message if failed */}
              {selectedPost.status === 'failed' && selectedPost.error && (
                <div className="p-4 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-400 space-y-1">
                  <div className="text-xs font-bold uppercase tracking-wider flex items-center gap-1">
                    <AlertCircle size={12} /> Pipeline Error
                  </div>
                  <p className="text-xs font-mono bg-rose-950/20 p-2.5 rounded-md border border-rose-900/30">
                    {selectedPost.error}
                  </p>
                </div>
              )}

              {/* AI Analysis Summary */}
              {selectedPost.ai_summary && (
                <div className="space-y-2">
                  <h4 className="text-sm font-bold uppercase tracking-wider text-slate-400">OpenRouter AI Summary</h4>
                  <div className="p-5 bg-slate-900/20 border border-slate-850 rounded-xl text-slate-300 text-sm leading-relaxed whitespace-pre-wrap font-sans">
                    {selectedPost.ai_summary}
                  </div>
                </div>
              )}

              {/* Video Transcript if present */}
              {selectedPost.content_type === 'video' && selectedPost.transcript && (
                <div className="space-y-2">
                  <h4 className="text-sm font-bold uppercase tracking-wider text-slate-400">Audio Transcription</h4>
                  <div className="p-4 bg-slate-950 border border-slate-900 rounded-xl text-slate-400 text-xs font-mono leading-relaxed max-h-48 overflow-y-auto whitespace-pre-wrap">
                    {selectedPost.transcript}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-slate-850 bg-slate-950/40 flex items-center justify-between">
              <span className="text-xs text-slate-500 font-mono">ID: {selectedPost.id}</span>

              {selectedPost.source !== 'ctolarsson' && (
                <button
                  onClick={(e) => handleReprocess(selectedPost.id, e)}
                  disabled={reprocessingId === selectedPost.id}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider border transition-all ${
                    reprocessingId === selectedPost.id
                      ? 'bg-slate-900 border-slate-850 text-slate-500 cursor-not-allowed'
                      : 'bg-slate-950 hover:bg-slate-900 border-slate-800 text-slate-200'
                  }`}
                >
                  <RotateCw size={12} className={reprocessingId === selectedPost.id ? 'animate-spin' : ''} />
                  Reprocess Content
                </button>
              )}
            </div>

          </div>
        </div>
      )}
    </div>
  )
}
